import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiJson } from '../api';
import { Shell } from '../components/Shell';
import './JourneyDetailPage.css';

type Step = {
  id: string;
  position: number;
  module_id: string;
  purpose: string | null;
  module_slug: string;
  module_title: string;
  recommended_depth?: string | null;
  micro_module_id?: string | null;
  micro_title?: string | null;
  micro_estimated_minutes?: number | null;
};

export function JourneyDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState<{ title: string; description?: string | null; steps: Step[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ lastStepPosition: number } | null>(null);

  const modLink = (moduleSlug: string) =>
    `/m/${moduleSlug}${slug ? `?journey=${encodeURIComponent(slug)}` : ''}`;

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<{
          journey: { title: string; description?: string | null };
          steps: Step[];
        }>(`/api/journeys/${slug}`);
        if (!cancelled)
          setData({
            title: res.journey.title,
            description: res.journey.description,
            steps: res.steps,
          });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const st = await apiJson<{ state: { lastStepPosition?: number } }>(`/api/journeys/${slug}/state`);
        if (!cancelled) setProgress(st.state?.lastStepPosition ? { lastStepPosition: st.state.lastStepPosition } : null);
      } catch {
        if (!cancelled) setProgress(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <Shell>
      <div className="wi-journey-detail">
        {!data && !error ? (
          <div className="wi-journey-detail-inner">
            <p className="muted">Loading…</p>
          </div>
        ) : null}
        {error ? <p className="wi-journey-detail-error">{error}</p> : null}
        {data ? (
          <>
            <header className="wi-journey-detail-header">
              <div className="wi-journey-detail-inner">
                <p className="wi-journey-detail-eyebrow">Guided journey</p>
                <h1 className="wi-journey-detail-title">{data.title}</h1>
                {data.description ? <p className="wi-journey-detail-lede">{data.description}</p> : null}
                <p className="wi-journey-detail-meta">
                  {data.steps.length} step{data.steps.length === 1 ? '' : 's'} · move in order, or jump ahead when you are
                  ready
                </p>
                {progress?.lastStepPosition && data.steps.length ? (
                  <p className="wi-journey-resume">
                    Last opened step {progress.lastStepPosition}.{' '}
                    <Link
                      to={modLink(
                        data.steps[Math.min(progress.lastStepPosition, data.steps.length) - 1].module_slug
                      )}
                    >
                      Resume
                    </Link>
                  </p>
                ) : null}
                {progress?.lastStepPosition ? (
                  <div className="wi-journey-progress" aria-hidden>
                    <div
                      className="wi-journey-progress__bar"
                      style={{
                        width: `${Math.min(100, Math.round((progress.lastStepPosition / data.steps.length) * 100))}%`,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </header>

            <section className="wi-journey-timeline" aria-label="Journey steps">
              <div className="wi-journey-detail-inner">
                <ol className="wi-journey-steps">
                  {data.steps.map((s, idx) => (
                    <li key={s.id} className="wi-journey-step">
                      <div className="wi-journey-step__marker" aria-hidden>
                        <span className="wi-journey-step__num">{s.position}</span>
                      </div>
                      <div className="wi-journey-step__body">
                        <div className="wi-journey-step__head">
                          <span className="wi-journey-step__label">
                            Step {s.position} of {data.steps.length}
                          </span>
                          {idx > 0 ? (
                            <Link to={modLink(data.steps[idx - 1].module_slug)} className="wi-journey-step__back">
                              ← Previous
                            </Link>
                          ) : null}
                        </div>
                        <h2 className="wi-journey-step__title">
                          <Link to={modLink(s.module_slug)}>{s.module_title}</Link>
                        </h2>
                        {s.recommended_depth || s.micro_title ? (
                          <p className="wi-journey-step__depth muted">
                            {s.recommended_depth ? (
                              <span>
                                Suggested depth: <strong>{s.recommended_depth}</strong>
                              </span>
                            ) : null}
                            {s.recommended_depth && s.micro_title ? ' · ' : null}
                            {s.micro_title ? (
                              <span>
                                Focus: {s.micro_title}
                                {typeof s.micro_estimated_minutes === 'number'
                                  ? ` (~${s.micro_estimated_minutes} min)`
                                  : ''}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        {s.purpose ? <p className="wi-journey-step__purpose">{s.purpose}</p> : null}
                        <div className="wi-journey-step__actions">
                          <Link to={modLink(s.module_slug)} className="wi-journey-step__primary">
                            Open module
                          </Link>
                          {idx < data.steps.length - 1 ? (
                            <Link to={modLink(data.steps[idx + 1].module_slug)} className="wi-journey-step__next">
                              Next: {data.steps[idx + 1].module_title} →
                            </Link>
                          ) : (
                            <span className="wi-journey-step__end">End of journey</span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            <footer className="wi-journey-detail-footer">
              <div className="wi-journey-detail-inner">
                <Link to="/journeys" className="wi-journey-detail-all">
                  All journeys
                </Link>
              </div>
            </footer>
          </>
        ) : null}
      </div>
    </Shell>
  );
}
